// COM-561: lets existing users of an EMR whose integration was rebuilt switch
// between the old and new one for a window (30 days from the launch popup),
// after which the backend keeps them on the new one. Three pieces: a launch
// popup shown once, a banner under the top bar, and "Why switch back?" when
// leaving the new integration. The backend (/v1/user/stats integrationSwitch)
// decides who sees it; instructions carry over on the first new scan
// (utils/legacySubsectionInstructions.js).
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GlobalContext } from 'api/GlobalContext.js';

// Per-EMR copy; the backend sends the key.
export const SWITCH_COPY = {
  prompt: {
    name: 'Prompt',
    whatsNew: [
      'Creates short and long term goals',
      'Fills objective tables (ROM, MMT and more)',
    ],
    whyBody:
      'Same Prompt integration you know, it just also writes your goals and fills objective tables now.',
    // Help article is still TBD; the link is hidden until it is set.
    whatsNewUrl: null,
  },
};

export const WHY_REASONS = [
  ['goals', 'Goals aren’t right'],
  ['tables', 'Objective tables aren’t right'],
  ['myself', 'I’d rather fill these myself'],
  ['broken', 'Something’s broken'],
  ['other', 'Other (e.g. wrong fields, too many changes)'],
];

const GREEN = '#2A9A68';
const FONT = "'Nunito Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";

const dayLabel = (days) => (days === 1 ? '1 day' : `${days} days`);

// How long the banner says "Switching to…" after a switch, so it's noticed.
export const SWITCHING_MS = 2000;

/** Banner text and colors for a switch state (the design's "Banner rules"). */
export function bannerFor({ onNew, daysLeft }, name, switchingTo) {
  if (switchingTo) {
    // No right-hand text, so the label has the whole width.
    return {
      label: `Switching to ${switchingTo} ${name} integration`,
      sub: null,
      tone: 'gray',
      bold: false,
      spinner: true,
    };
  }
  if (onNew) {
    return {
      label: `Switch to old ${name} integration`,
      sub: `${dayLabel(daysLeft)} left`,
      tone: 'gray',
      bold: false,
    };
  }
  if (daysLeft <= 1) {
    return {
      label: `Switching to new ${name} integration tomorrow`,
      sub: 'Switch now',
      tone: 'amber',
      bold: true,
    };
  }
  if (daysLeft <= 3) {
    return {
      label: `Switching to new ${name} integration in ${dayLabel(daysLeft)}`,
      sub: 'Switch now',
      tone: 'amber',
      bold: false,
    };
  }
  return {
    label: `Switch to new ${name} integration`,
    sub: `${dayLabel(daysLeft)} left`,
    tone: 'gray',
    bold: false,
  };
}

const TONES = {
  gray: { background: '#F7F9F8', color: '#3A4452', border: '#D6DCE2' },
  amber: { background: '#FFF4D6', color: '#8A5A00', border: '#F0C877' },
};

const Dialog = ({ labelledBy, children }) =>
  ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: FONT,
      }}
    >
      <div
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, background: 'rgba(20,30,40,0.35)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 340,
          boxSizing: 'border-box',
          background: '#FFFFFF',
          borderRadius: 20,
          boxShadow: '0 20px 48px rgba(20,30,40,0.25)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          color: '#1C2430',
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );

const buttonStyle = (primary, disabled) => ({
  flex: 1,
  height: 42,
  border: primary ? 0 : '1px solid #E3E7EC',
  borderRadius: 10,
  background: primary ? GREEN : '#FFFFFF',
  color: primary ? '#FFFFFF' : '#1C2430',
  fontSize: 14,
  fontWeight: primary ? 800 : 700,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const Check = () => (
  <i className="fas fa-check" aria-hidden="true" style={{ color: '#1A7A52', fontSize: 13 }} />
);

const IntegrationSwitch = () => {
  const { apiClient } = useContext(GlobalContext);
  const [state, setState] = useState(apiClient.integrationSwitch || null);
  const [whyOpen, setWhyOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);
  const [toast, setToast] = useState('');
  const shownLogged = useRef(false);
  const toastTimer = useRef(null);

  useEffect(() => {
    const handler = (client) => setState(client.integrationSwitch || null);
    apiClient.onProfileUpdated?.(handler);
    handler(apiClient);
    return () => {
      apiClient.offProfileUpdated?.(handler);
      clearTimeout(toastTimer.current);
    };
  }, [apiClient]);

  const copy = state && SWITCH_COPY[state.emr];
  const launchOpen = Boolean(copy && state.showLaunch);

  // The window starts the first time the launch popup is seen.
  useEffect(() => {
    if (!launchOpen || shownLogged.current) return;
    shownLogged.current = true;
    apiClient.saveIntegrationSwitch('shown');
  }, [launchOpen, apiClient]);

  if (!copy) return null;

  const showToast = (message) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  };

  // `switchingTo` ('new' / 'old') shows "Switching to…" on the banner while the
  // switch saves and for at least SWITCHING_MS; `onSaved` runs as soon as it saved.
  const act = async (action, message, extra, switchingTo, onSaved) => {
    if (busy) return false;
    setBusy(true);
    setSwitchingTo(switchingTo || null);
    const started = Date.now();
    const response = await apiClient.saveIntegrationSwitch(action, extra);
    const ok = Boolean(response && !response.error);
    if (ok) {
      onSaved?.();
      if (switchingTo) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.max(0, SWITCHING_MS - (Date.now() - started))),
        );
      }
    }
    setSwitchingTo(null);
    setBusy(false);
    if (!ok) {
      showToast('Couldn’t switch right now. Please try again.');
      return false;
    }
    if (message) showToast(message);
    return true;
  };

  const onBanner = () => {
    if (state.onNew) {
      setReason(null);
      setComment('');
      setWhyOpen(true);
    } else {
      act('switch_to_new', `Switched to the new ${copy.name} integration`, undefined, 'new');
    }
  };

  const onWhySwitch = async () => {
    const trimmed = comment.trim();
    await act(
      'switch_to_old',
      reason || trimmed
        ? 'Switched back · thanks for the feedback'
        : `Switched to the old ${copy.name} integration`,
      { reason: reason || undefined, comment: trimmed || undefined },
      'old',
      () => setWhyOpen(false),
    );
  };

  const banner = bannerFor(state, copy.name, switchingTo);
  const tone = TONES[banner.tone];

  return (
    <>
      {/* #navbar-main is absolute at the top of main-content: move it below
          the banner, or it covers the banner and swallows its clicks. */}
      <style>{'.main-content > #navbar-main { top: 32px; }'}</style>
      <button
        type="button"
        onClick={onBanner}
        disabled={busy}
        data-testid="integration-switch-banner"
        style={{
          width: '100%',
          height: 32,
          padding: '0 12px 0 16px',
          border: 0,
          borderTop: `1px solid ${tone.border}`,
          borderBottom: `1px solid ${tone.border}`,
          background: tone.background,
          color: tone.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          fontFamily: FONT,
          fontSize: 12,
          fontWeight: banner.bold ? 800 : 700,
          textAlign: 'left',
          cursor: switchingTo ? 'default' : 'pointer',
        }}
      >
        <span
          role={switchingTo ? 'status' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {banner.bold && <i className="far fa-clock" aria-hidden="true" />}
          {banner.spinner && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
          {banner.label}
        </span>
        {banner.sub && (
          <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
            {banner.sub}
            <i className="fas fa-chevron-right" aria-hidden="true" style={{ fontSize: 10 }} />
          </span>
        )}
      </button>

      {launchOpen && (
        <Dialog labelledBy="integration-switch-launch-title">
          <span id="integration-switch-launch-title" style={{ fontSize: 18, fontWeight: 800 }}>
            Meet the new {copy.name} integration
          </span>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#F3FAF6',
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: '#1A7A52',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              What’s new
            </span>
            {copy.whatsNew.map((line) => (
              <span
                key={line}
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700 }}
              >
                <Check />
                {line}
              </span>
            ))}
          </div>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700 }}>
            <i className="fas fa-shield-alt" aria-hidden="true" style={{ color: '#1A7A52' }} />
            Your note settings haven’t changed
          </span>
          {copy.whatsNewUrl && (
            <a
              href={copy.whatsNewUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#1A7A52', fontSize: 13, fontWeight: 700 }}
            >
              See what's new ↗
            </a>
          )}
          <span style={{ fontSize: 12, lineHeight: 1.4, color: '#7A8491' }}>
            Not ready? Keep the current integration for another{' '}
            {dayLabel(state.daysLeft)} and switch whenever you like.
          </span>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button
              type="button"
              disabled={busy}
              style={buttonStyle(false, busy)}
              onClick={() =>
                act('not_now', 'No problem. Switch anytime from the bar at the top.')
              }
            >
              Not now
            </button>
            <button
              type="button"
              disabled={busy}
              style={buttonStyle(true, busy)}
              onClick={() => act('try_it', null, undefined, 'new')}
            >
              Try it
            </button>
          </div>
        </Dialog>
      )}

      {whyOpen && (
        <Dialog labelledBy="integration-switch-why-title">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span id="integration-switch-why-title" style={{ fontSize: 17, fontWeight: 800 }}>
                Why switch back?
              </span>
              <span style={{ fontSize: 13, lineHeight: 1.4, color: '#5B6573' }}>
                {copy.whyBody}
              </span>
            </div>
            <button
              type="button"
              aria-label="Close, keep the new integration"
              onClick={() => setWhyOpen(false)}
              style={{
                width: 32,
                height: 32,
                margin: '-6px -6px 0 0',
                border: 0,
                borderRadius: 8,
                background: 'transparent',
                color: '#5B6573',
                cursor: 'pointer',
              }}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
          <div
            role="radiogroup"
            aria-labelledby="integration-switch-why-title"
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {WHY_REASONS.map(([id, label]) => {
              const on = reason === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setReason(id)}
                  style={{
                    minHeight: 40,
                    padding: '8px 12px',
                    border: `1px solid ${on ? GREEN : '#E3E7EC'}`,
                    borderRadius: 10,
                    background: on ? '#E6F5EE' : '#FFFFFF',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontFamily: FONT,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1C2430',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      boxSizing: 'border-box',
                      borderRadius: 999,
                      border: on ? `5px solid ${GREEN}` : '1.5px solid #9AA4AF',
                      background: '#FFFFFF',
                    }}
                  />
                  {label}
                </button>
              );
            })}
          </div>
          {reason && (
            <textarea
              aria-label="Tell us more"
              value={comment}
              maxLength={2000}
              onChange={(e) => setComment(e.target.value)}
              placeholder={reason === 'other' ? 'Tell us more (optional)' : 'Anything else? (optional)'}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                minHeight: 56,
                resize: 'none',
                padding: '8px 10px',
                border: '1.5px solid #C7E8D9',
                borderRadius: 10,
                fontFamily: FONT,
                fontSize: 14,
                lineHeight: 1.4,
                color: '#1C2430',
              }}
            />
          )}
          <div style={{ display: 'flex', marginTop: 4 }}>
            <button
              type="button"
              disabled={busy}
              style={buttonStyle(true, busy)}
              onClick={onWhySwitch}
            >
              Switch
            </button>
          </div>
        </Dialog>
      )}

      {toast &&
        ReactDOM.createPortal(
          <div
            role="status"
            style={{
              position: 'fixed',
              left: 16,
              right: 16,
              bottom: 16,
              zIndex: 10060,
              maxWidth: 420,
              margin: '0 auto',
              padding: '10px 14px',
              borderRadius: 12,
              background: '#1C2430',
              color: '#FFFFFF',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {toast}
          </div>,
          document.body,
        )}
    </>
  );
};

export default IntegrationSwitch;
